import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import {
  StatCard,
  Panel,
  PanelEmpty,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  distributionBarOptions,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../component/common/designSystem';
import {
  SLEEP_QUALITY_RAMP,
  SLEEP_SOURCE_COLORS,
  SLEEP_DURATION_RAMP,
  SLEEP_SCORE_RAMP,
  TIME_OF_DAY_RAMP,
  CONSISTENCY_RAMP,
  sleepTargetAnnotation
} from './sleepTheme';
import {
  fetchTrackerDashboard,
  formatBigNumber,
  minsToHhMm,
  lastNDaysLabels
} from './sleepHubApi';

/**
 * Sleep → Tracker Dashboard.
 *
 * Reads as three bands:
 *  1. Headline stats — the population and its averages.
 *  2. Trends over the last 30 days — duration, score, volume, weekday shape.
 *  3. Distributions — how nights are spread across duration, score, quality,
 *     bedtime, wake time, log source, and how consistently people log at all.
 *
 * Reused by the company panel via the `fetcher` prop (company-scoped data);
 * `populationLabel` names the scoped population (e.g. "employees").
 */
const TrackerDashboard = ({ fetcher = fetchTrackerDashboard, populationLabel = 'users' }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const durationTrend = stats?.avgDurationTrend || [];
  const scoreTrend = stats?.scoreTrend || [];
  const logsPerDay = stats?.logsPerDay || [];
  const quality = stats?.qualityDistribution || [];
  const sources = stats?.deviceSourceSplit || [];
  const duration = stats?.durationDistribution || [];
  const score = stats?.scoreDistribution || [];
  const bedtime = stats?.bedtimeDistribution || [];
  const wake = stats?.wakeTimeDistribution || [];
  const weekday = stats?.weekdayPattern || [];
  const consistency = stats?.trackingConsistency || [];
  const wearableProviders = stats?.wearableProviders || [];

  // People who linked a device but whose logs are all manual — the device is
  // connected and silent, which is a support problem, not a usage one.
  const silentWearables = Math.max(
    (stats?.connectedWearableUsers || 0) - (stats?.wearableUsers || 0),
    0
  );
  // Tracking sleep, but every log typed in by hand.
  const manualOnly = Math.max((stats?.usersTracking || 0) - (stats?.wearableUsers || 0), 0);

  const trendLabels = lastNDaysLabels(durationTrend.length);

  /**
   * Duration and score share one chart on two axes: the question people
   * actually ask is whether longer nights are scoring better, and that only
   * reads if the two lines sit on top of each other.
   */
  const durationOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.brand, CHART_COLORS.greenDeep],
    stroke: { curve: 'smooth', width: [2, 2], dashArray: [0, 4] },
    fill: { type: ['gradient', 'solid'], gradient: AREA_FILL.gradient },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: trendLabels, tickAmount: 6 },
    yaxis: [
      {
        seriesName: 'Avg duration',
        labels: { formatter: (val) => minsToHhMm(val) },
        title: { text: 'Duration' }
      },
      {
        seriesName: 'Avg score',
        opposite: true,
        min: 0,
        max: 10,
        labels: { formatter: (val) => `${Math.round(val * 10) / 10}` },
        title: { text: 'Score' }
      }
    ],
    tooltip: {
      shared: true,
      y: [
        { formatter: (val) => minsToHhMm(val) },
        { formatter: (val) => `${val} / 10` }
      ]
    },
    annotations: sleepTargetAnnotation
  };

  const logsOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brandSoft],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: trendLabels, tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} logs` } }
  };

  /**
   * Weekday: volume as columns, average duration as a line on its own axis.
   * Friday and Saturday nights are usually the story here, and one chart
   * showing both keeps "fewer logs" from being mistaken for "worse sleep".
   */
  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.brand, CHART_COLORS.orange],
    stroke: { width: [0, 3], curve: 'smooth' },
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: weekday.map((day) => day.label) },
    yaxis: [
      { seriesName: 'Nights logged', labels: { formatter: (val) => formatBigNumber(val) } },
      {
        seriesName: 'Avg duration',
        opposite: true,
        labels: { formatter: (val) => minsToHhMm(val) }
      }
    ],
    tooltip: {
      shared: true,
      y: [
        { formatter: (val) => `${val.toLocaleString()} nights` },
        { formatter: (val) => minsToHhMm(val) }
      ]
    }
  };

  const bedtimeOptions = distributionBarOptions({
    labels: bedtime.map((band) => band.label),
    colors: TIME_OF_DAY_RAMP,
    unit: 'nights'
  });

  const wakeOptions = distributionBarOptions({
    labels: wake.map((band) => band.label),
    colors: TIME_OF_DAY_RAMP,
    unit: 'nights'
  });

  const targetLabel = stats?.targetMins ? minsToHhMm(stats.targetMins) : '7h 0m';

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Sleep Tracker Dashboard | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.logs}
          title="Total Sleep Logs"
          value={stats ? formatBigNumber(stats.totalLogs) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.users}
          title={`${populationLabel === 'users' ? 'Users' : 'People'} Tracking Sleep`}
          value={stats ? formatBigNumber(stats.usersTracking) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Avg Sleep Duration"
          value={stats ? minsToHhMm(stats.avgSleepMins) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Avg Sleep Score"
          value={stats ? `${stats.avgSleepScore} / 10` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.wearable}
          title="Wearables Connected"
          value={stats ? formatBigNumber(stats.connectedWearableUsers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Common Bedtime"
          value={stats?.commonBedtime || '-'}
        />
      </div>

      {/* ---------- Distribution summary ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <MiniDonutCard
          title="Sleep Duration"
          subtitle="Nights by length"
          data={duration}
          colors={SLEEP_DURATION_RAMP}
          unit="nights"
        />
        <MiniDonutCard
          title="Sleep Score"
          subtitle="Scored nights, 0–10"
          data={score}
          colors={SLEEP_SCORE_RAMP}
          unit="nights"
        />
        <MiniDonutCard
          title="Sleep Quality"
          subtitle="As rated by the user"
          data={quality}
          colors={SLEEP_QUALITY_RAMP}
          unit="logs"
        />
        <MiniDonutCard
          title="Log Source"
          subtitle="Manual vs wearable"
          data={sources}
          colors={SLEEP_SOURCE_COLORS}
          unit="logs"
        />
        <MiniDonutCard
          title="Tracking Consistency"
          subtitle="Nights logged in 30 days"
          data={consistency}
          colors={CONSISTENCY_RAMP}
          unit={populationLabel}
        />
        <MiniDonutCard
          title="Connected Devices"
          subtitle="Live wearable integrations"
          data={wearableProviders}
          unit={populationLabel}
        />
      </div>

      {/* ---------- Trends ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel
          title="Sleep Duration & Score"
          subtitle={`Average across ${populationLabel} — last 30 days`}
        >
          {durationTrend.length > 0 ? (
            <Chart
              type="line"
              height={340}
              width="100%"
              series={[
                { name: 'Avg duration', type: 'area', data: durationTrend },
                { name: 'Avg score', type: 'line', data: scoreTrend }
              ]}
              options={durationOptions}
            />
          ) : (
            <PanelEmpty>No sleep logs recorded yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Sleep Logs Per Day" subtitle="Manual + wearable — last 30 days">
          {logsPerDay.length > 0 ? (
            <Chart
              type="bar"
              height={340}
              width="100%"
              series={[{ name: 'Logs', data: logsPerDay }]}
              options={logsOptions}
            />
          ) : (
            <PanelEmpty>No sleep logs recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Patterns ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="Nights Hitting Target"
          subtitle={`Logs of ${targetLabel} or more`}
          value={stats ? `${stats.onTargetPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.onTargetLogs)} of ${formatBigNumber(
                stats.totalLogs
              )} nights`
              : null
          }
        />

        <Panel className="lg:col-span-2" title="Sleep by Weekday" subtitle="Which nights sleep best">
          {weekday.length > 0 ? (
            <Chart
              type="line"
              height={300}
              width="100%"
              series={[
                { name: 'Nights logged', type: 'column', data: weekday.map((day) => day.logs) },
                {
                  name: 'Avg duration',
                  type: 'line',
                  data: weekday.map((day) => day.avgDurationMins)
                }
              ]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No sleep logs recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Clock ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Panel title="Bedtime" subtitle="When nights start">
          {bedtime.some((band) => band.count > 0) ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Nights', data: bedtime.map((band) => band.count) }]}
              options={bedtimeOptions}
            />
          ) : (
            <PanelEmpty>No bedtimes recorded yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Wake Time" subtitle="When nights end">
          {wake.some((band) => band.count > 0) ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Nights', data: wake.map((band) => band.count) }]}
              options={wakeOptions}
            />
          ) : (
            <PanelEmpty>No wake times recorded yet.</PanelEmpty>
          )}
        </Panel>

        {/*
          Sync health, not device count: the number that matters is how many
          people linked a wearable and are getting nothing out of it.
        */}
        <Panel title="Wearable Sync Health" subtitle="Where sleep data is coming from">
          <ProgressList
            items={[
              { label: 'Syncing from a device', count: stats?.wearableUsers || 0 },
              { label: 'Connected but not syncing', count: silentWearables },
              { label: 'Logging manually only', count: manualOnly }
            ]}
            unit={populationLabel}
          />
        </Panel>
      </div>
    </div>
  );
};

export default TrackerDashboard;
